/** @format — Port recommendation badge ids, labels, and theme tokens */

export const PORT_BADGE_IDS = {
  BEST_CHOICE: "best_choice",
  CLOSEST_PORT: "closest_port",
  CHEAPEST: "cheapest",
  EARLIEST_SAILING: "earliest_sailing",
};

/** Max badges shown on multi-badge (white) cards */
export const MULTI_BADGE_MAX = 2;

/** 1 = themed border by default; 2 = neutral border until card hover (swap in PortOptionCard) */
export const PORT_CARD_BORDER_ITERATION = 2;

const NEUTRAL_CARD_BORDER = "border-[#e4e7ec]";

/** @type {Record<string, {
 *   label: string;
 *   badgeBg: string;
 *   badgeText: string;
 *   cardBorder: string;
 *   cardBorderHover: string;
 *   cardHoverBg: string;
 *   breakdownBg: string;
 *   ctaDefault: string;
 *   ctaActive: string;
 *   ctaPressed: string;
 *   focusRing: string;
 * }>} */
export const PORT_BADGE_THEMES = {
  [PORT_BADGE_IDS.BEST_CHOICE]: {
    label: "Best Choice",
    badgeBg: "bg-blue-50",
    badgeText: "text-blue-700",
    cardBorder: "border-blue-700",
    cardBorderHover: "hover:border-blue-700",
    cardHoverBg: "hover:bg-blue-50",
    breakdownBg: "bg-[#e7eef7]",
    ctaDefault: "bg-white border border-neutral-300 text-[#0a0a0a]",
    ctaActive: "group-hover:bg-blue-600 group-hover:text-neutral-50 group-hover:border-blue-600",
    ctaPressed: "hover:bg-blue-700 hover:border-blue-700 active:bg-blue-800 active:border-blue-800 active:scale-[0.98]",
    focusRing: "focus-visible:outline-blue-600",
  },
  [PORT_BADGE_IDS.CLOSEST_PORT]: {
    label: "Closest Port",
    badgeBg: "bg-violet-50",
    badgeText: "text-violet-700",
    cardBorder: "border-violet-700",
    cardBorderHover: "hover:border-violet-700",
    cardHoverBg: "hover:bg-violet-50",
    breakdownBg: "bg-[#edebf7]",
    ctaDefault: "bg-white border border-neutral-300 text-[#0a0a0a]",
    ctaActive: "group-hover:bg-violet-600 group-hover:text-neutral-50 group-hover:border-violet-600",
    ctaPressed:
      "hover:bg-violet-700 hover:border-violet-700 active:bg-violet-800 active:border-violet-800 active:scale-[0.98]",
    focusRing: "focus-visible:outline-violet-600",
  },
  [PORT_BADGE_IDS.CHEAPEST]: {
    label: "Cheapest",
    badgeBg: "bg-green-50",
    badgeText: "text-green-700",
    cardBorder: "border-green-700",
    cardBorderHover: "hover:border-green-700",
    cardHoverBg: "hover:bg-green-50",
    breakdownBg: "bg-[#e8f5ec]",
    ctaDefault: "bg-white border border-neutral-300 text-[#0a0a0a]",
    ctaActive: "group-hover:bg-green-700 group-hover:text-neutral-50 group-hover:border-green-700",
    ctaPressed:
      "hover:bg-green-800 hover:border-green-800 active:bg-green-900 active:border-green-900 active:scale-[0.98]",
    focusRing: "focus-visible:outline-green-700",
  },
  [PORT_BADGE_IDS.EARLIEST_SAILING]: {
    label: "Earliest Sailing Date",
    badgeBg: "bg-yellow-50",
    badgeText: "text-yellow-700",
    cardBorder: "border-yellow-700",
    cardBorderHover: "hover:border-yellow-700",
    cardHoverBg: "hover:bg-yellow-50",
    breakdownBg: "bg-[#f6f4e0]",
    ctaDefault: "bg-white border border-neutral-300 text-[#0a0a0a]",
    ctaActive: "group-hover:bg-yellow-700 group-hover:text-neutral-50 group-hover:border-yellow-700",
    ctaPressed:
      "hover:bg-yellow-800 hover:border-yellow-800 active:bg-yellow-900 active:border-yellow-900 active:scale-[0.98]",
    focusRing: "focus-visible:outline-yellow-700",
  },
};

/** Card shell classes — iteration 1: themed border default; iteration 2: neutral until hover */
export function getPortCardSurfaceClasses(theme) {
  if (!theme) return `bg-white ${NEUTRAL_CARD_BORDER}`;
  if (PORT_CARD_BORDER_ITERATION === 2) {
    return `bg-white ${NEUTRAL_CARD_BORDER} ${theme.cardBorderHover} ${theme.cardHoverBg}`;
  }
  return `bg-white ${theme.cardBorder} ${theme.cardHoverBg}`;
}

const BADGE_ORDER = [
  PORT_BADGE_IDS.BEST_CHOICE,
  PORT_BADGE_IDS.CLOSEST_PORT,
  PORT_BADGE_IDS.CHEAPEST,
  PORT_BADGE_IDS.EARLIEST_SAILING,
];

/**
 * @param {object} option
 * @returns {{ badges: string[]; primaryBadge: string|null; isMixedBadges: boolean; theme: object|null }}
 */
export function resolvePortOptionPresentation(option) {
  let badges = Array.isArray(option?.badges)
    ? option.badges.filter((b) => typeof b === "string" && PORT_BADGE_THEMES[b])
    : [];

  if (!badges.length && option?.highlight === "popular") {
    badges = [PORT_BADGE_IDS.BEST_CHOICE];
  }

  badges = BADGE_ORDER.filter((id) => badges.includes(id));

  const isMixedBadges = badges.length > 1;

  if (isMixedBadges) {
    badges = badges.slice(0, MULTI_BADGE_MAX);
  }

  const primaryBadge =
    option?.primaryBadge && badges.includes(option.primaryBadge) ? option.primaryBadge : (badges[0] ?? null);

  const theme = primaryBadge ? PORT_BADGE_THEMES[primaryBadge] : null;

  return { badges, primaryBadge, isMixedBadges, theme };
}
